import React, { useState } from 'react';
import { Col, Form, Input, Layout, Modal, notification, Row, Space, Tooltip, Typography } from 'antd';
import { CopyFilled, DeleteFilled, EditFilled } from '@ant-design/icons';
import BreadcrumbContainer from '../../../containers/Breadcrumb';
import { CustomButton } from '../../../components';
import AjaxTable from '../../../components/AjaxTable';
import { ajaxTableStructure, createStructure, deleteStructure, updateStructure } from '../../../api/module/structure';
import MESSAGES from '../../../enums/Messages';
import { catchException } from '../../../utils/helpers/helpers';

const { Content } = Layout;
const { Title, Paragraph } = Typography;

const ModalStructure = ({ handleReset, onFinish, data, loading, isCloning, isEditing }) => {
  const [form] = Form.useForm();
  return (
    <>
      <Form
        form={form}
        layout="horizontal"
        name="structureForm"
        onFinish={onFinish}
        initialValues={data}
        preserve={false}
      >
        <Row gutter={40}>
          <Col span={8}>
            <Form.Item
              label="Código"
              name="codigo"
              rules={[
                { required: true, message: 'Por favor ingrese un código' },
                { min: 3, message: 'El código debe tener al menos 3 caracteres' },
                { max: 10, message: 'El código no puede tener más de 10 caracteres' }
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={16}>
            <Form.Item
              label="Tipo"
              name="tipo"
              rules={[
                { required: true, message: 'Por favor ingrese un tipo de estructura' },
                { max: 255, message: 'El tipo de estructura no puede tener más de 255 caracteres' }
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row justify={'space-between'}>
          <Col span={11}>
            <Form.Item
              label="Detalle 1"
              name="detalle1"
              rules={[
                { required: true, message: 'Por favor ingrese un detalle' },
                { max: 100, message: 'El detalle no puede tener más de 100 caracteres' }
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={11}>
            <Form.Item
              label="Detalle 2"
              name="detalle2"
              rules={[
                { max: 100, message: 'El detalle no puede tener más de 100 caracteres' }
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row justify={'space-between'}>
          <Col span={11}>
            <Form.Item
              label="Detalle 3"
              name="detalle3"
              rules={[
                { max: 100, message: 'El detalle no puede tener más de 100 caracteres' }
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={11}>
            <Form.Item
              label="Detalle 4"
              name="detalle4"
              rules={[
                { max: 100, message: 'El detalle no puede tener más de 100 caracteres' }
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row justify={'center'}>
          <Col span={3}>
            <Form.Item>
              <CustomButton
                type="primary"
                htmlType="submit"
                text={isEditing ? 'Guardar' : isCloning ? 'Clonar' : 'Guardar'}
                loading={loading}
              />
            </Form.Item>
          </Col>
          <Col span={3}>
            <Form.Item>
              <CustomButton
                type="default"
                htmlType="button"
                text="Volver"
                onClick={handleReset}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
      </>);
}

export default function StructureList({ history }) {
  const [openStructureModal, setOpenStructureModal] = useState(false);
  const [reloadStructure, setReloadStructure] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isCloning, setIsCloning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});

  const breadcrumbItems = [
    {
      text: 'Unidades - Estructura',
    },
  ];

  const columnsStructure = [
    {
      title: 'Código',
      dataIndex: 'codigo',
      key: 'codigo',
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
    },
    {
      title: 'Detalle 1',
      dataIndex: 'detalle1',
      key: 'detalle1',
    },
    {
      title: 'Detalle 2',
      dataIndex: 'detalle2',
      key: 'detalle2',
    },
    {
      title: 'Detalle 3',
      dataIndex: 'detalle3',
      key: 'detalle3',
    },
    {
      title: 'Detalle 4',
      dataIndex: 'detalle4',
      key: 'detalle4',
    },
    {
      title: 'Acciones',
      dataIndex: 'accion',
      align: 'center',
      fixed: 'right',
      width: 125,
      render: (_, record) => (
        <Space>
          <Tooltip title='Editar Unidad' placement='top'>
            <EditFilled onClick={() => handleEdit(record)} style={{ color: 'green', fontSize: '16px' }} />
          </Tooltip>
          <Tooltip title='Eliminar Unidad' placement='top'>
            <DeleteFilled onClick={() => handleDelete(record)} style={{ color: 'red', fontSize: '16px' }} />
          </Tooltip>
          <Tooltip title='Clonar Unidad' placement='top'>
            <CopyFilled onClick={() => handleCopy(record)} style={{ color: 'blue', fontSize: '16px' }} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleEdit = (record) => {
    setIsEditing(true);
    setData(record);
    setOpenStructureModal(true);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Eliminar estructura',
      content: (
        <div>
          <Paragraph>
            ¿Está seguro que desea eliminar la estructura <b>{record.tipo}</b>?
          </Paragraph>
          <Paragraph>
            <b>Nota:</b> Esta acción no se puede deshacer.
          </Paragraph>
        </div>
      ),
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await deleteStructure(record.id);
          notification.success({
            message: MESSAGES.SUCCESS_MSG,
            description: MESSAGES.DESC_SUCCESS_MSG,
            duration: 2,
          });
          setReloadStructure({});
        } catch (error) {
          notification.error({
            message: "Error",
            description: "No se puede eliminar la estructura porque tiene unidades asignadas.",
            duration: 4,
          });
        }
      },
    });
  };

  const handleCopy = (record) => {
    const { codigo, tipo, ...rest } = record;
    setIsCloning(true);
    setData(rest);
    setOpenStructureModal(true);
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      if (!isEditing) {
        await createStructure(values);
        notification.success({
          message: MESSAGES.SUCCESS_MSG,
          description: MESSAGES.DESC_SUCCESS_MSG,
          duration: 2,
        });
      } else {
        await updateStructure(data.id, values);
        notification.success({
          message: MESSAGES.SUCCESS_MSG,
          description: MESSAGES.DESC_SUCCESS_MSG,
          duration: 2,
        });
      }
    } catch (error) {
      catchException(error);
    } finally {
      handleReset();
      setReloadStructure({});
      setLoading(false);
    }
  };

  const handleReset = () => {
    setData({});
    setIsEditing(false);
    setIsCloning(false);
    setOpenStructureModal(false);
  };

  return (
    <Content>
      <Row>
        <Col span={24}>
          <BreadcrumbContainer items={breadcrumbItems} />
        </Col>
        <Col span={24}>
          <Title className={'text-uppercase'} level={2}>
            Unidades - Estructura
          </Title>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <CustomButton
            type="primary"
            htmlType="button"
            text="+ Crear estructura unidad"
            onClick={() => {
              setOpenStructureModal(true);
            }}
          />
        </Col>
      </Row>
      <Row style={{ marginTop: '20px' }}>
        <Col span={24}>
          <AjaxTable
            tableTitle={'Listado de estructuras'}
            rowKey={'id'}
            endpoint={ajaxTableStructure}
            columns={columnsStructure}
            searchInput={true}
            reloadSource={reloadStructure}
          />
        </Col>
      </Row>
      <Modal
        title={isEditing ? 'Editar Unidad Estructura' : isCloning ?
                           'Clonar Unidad Estructura' : 'Agregar Unidad Estructura'}
        visible={openStructureModal}
        onCancel={handleReset}
        footer={null}
        width={'70%'}
        destroyOnClose
      >
        <ModalStructure
          onFinish={onFinish}
          data={data}
          loading={loading}
          isEditing={isEditing}
          isCloning={isCloning}
          handleReset={handleReset}
        />
      </Modal>
    </Content>
  );
}
